import { ShoppingBag } from 'lucide-react'
import { useCart } from '@/contexts/CartContext'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'

interface CartIconProps {
  className?: string
}

export default function CartIcon({ className = '' }: CartIconProps) {
  const { toggleCart, getItemCount, items } = useCart()
  const { t } = useTranslation('cart')
  const itemCount = getItemCount()

  return (
    <button
      onClick={toggleCart}
      aria-label={t('cartAria', { count: itemCount })}
      className={cn(
        'relative grid h-9 w-9 place-items-center rounded-lg text-garage-cream/80 transition-colors hover:bg-garage-card/40 hover:text-garage-cream',
        className
      )}
    >
      <ShoppingBag className="h-5 w-5" />

      {/* Item count badge */}
      <AnimatePresence>
        {itemCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className="absolute -right-0.5 -top-0.5 grid h-4 min-w-[1rem] place-items-center rounded-full bg-garage-accent px-1 text-[10px] font-bold leading-none text-white shadow-sm shadow-black/30"
          >
            {itemCount > 99 ? '99+' : itemCount}
          </motion.span>
        )}
      </AnimatePresence>

      {/* Pulse animation when items added */}
      <AnimatePresence>
        {items.length > 0 && (
          <motion.span
            key={items[items.length - 1]?.id}
            initial={{ scale: 1, opacity: 0.4 }}
            animate={{ scale: 1.8, opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="pointer-events-none absolute inset-0 rounded-lg bg-garage-accent"
          />
        )}
      </AnimatePresence>
    </button>
  )
}
